import {
  Component,
  Host,
  h,
  Prop,
  State,
  EventEmitter,
  Event,
} from '@stencil/core';
import {
  BasketModel,
  BasketService,
  Language,
  SessionRepository,
  SessionModel,
  SessionService,
} from '@cesare';
import { SessionHelperService } from '@cesare';
import { QwButton } from '../shared/qw-button/qw-button';
import { catchError, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { tap, of } from 'rxjs';
import {
  sessionStorageRemoveItem,
  sessionStorageSetItem,
} from '../../core/storage/storage.service';
import { loadingService } from '../../core/qw-loading-manager/qw-loading.service';
import { isAddingPromo$ } from '../../core/app/app';

@Component({
  tag: 'qw-promo-code',
  styleUrl: 'qw-promo-code.scss',
  shadow: false,
})
export class QwPromoCode {
  @Prop() qwPromoCodeLabel: string;
  @State() session: SessionModel;
  @State() basket: BasketModel;
  @State() promoCodeValue: string = window.QW_HOTEL_ENV.PROMO_CODE_DEFAULT
    ? window.QW_HOTEL_ENV.PROMO_CODE_DEFAULT
    : '';
  @State() hasPromo: boolean;
  @State() promoCodeLoading: boolean = false;
  @State() promoCodeFeedback = '';
  @State() componentLoading: boolean = true;
  @State() renderTriggered: boolean = false;
  @State() isUserTyping: boolean = false;
  @State() isPromoInvalid = false;
  @Event({ eventName: 'qwMessageNotification', composed: true })
  qwMessageNotification: EventEmitter<string>;
  @Event({ eventName: 'qwPromoCodeAdded', composed: true })
  qwPromoCodeAdded: EventEmitter<string>;
  @Event({ eventName: 'qwPromoCodeRemoved', composed: true })
  qwPromoCodeRemoved: EventEmitter<string>;

  public componentWillLoad() {
    this.setServiceLoading(true);

    SessionRepository.sessionLoaded$
      .pipe(distinctUntilChanged())
      .subscribe((session) => {
        this.componentLoading = false;
        this.setServiceLoading(this.componentLoading);

        this.session = session;
        this.hasPromo = this.hasPromoCode();
        if (this.hasPromo) {
          this.promoCodeValue = this.session.context.promoCode;
        }
        BasketService.getBasket(session)
          .pipe(distinctUntilChanged())
          .subscribe((basket) => {
            this.basket = basket;
          });
      });

    loadingService.loadComplete$.subscribe((res) => {
      this.renderTriggered = res;
    });
  }

  private setServiceLoading(isLoading: boolean) {
    loadingService.setLoading({
      idComponent: 'promo-code',
      isLoading,
    });
  }

  private hasPromoCode() {
    return !!this.session.context.promoCode;
  }

  private onChangeValue(event) {
    this.isPromoInvalid = false;
    this.promoCodeValue = event.target.value;
    this.isUserTyping = this.promoCodeValue.length > 2 ? true : false;
  }

  private setPromoCodeFeedback(res, customFeedback) {
    this.setServiceLoading(false);
    this.promoCodeFeedback =
      res.status === 400 ? res.body.Message : customFeedback;

    if (res.status === 400) {
      isAddingPromo$.next(false);
      this.isPromoInvalid = true;
    } else {
      window.location.reload();
    }
  }

  private managePromo(action) {
    isAddingPromo$.next(true);
    if (this.basket.rooms.length > 0) {
      BasketService.deleteBasket().subscribe({
        next: () => {
          this.fetchSessionWithPromoCode(action);
        },
      });
    } else {
      this.fetchSessionWithPromoCode(action);
    }
  }

  private fetchSessionWithPromoCode(action) {
    this.promoCodeLoading = true;
    if (action === 'remove') {
      this.promoCodeValue = '';
      sessionStorageRemoveItem('promoCode');
    }
    return SessionHelperService.fetchUpdateSessionContext(
      this.session?.sessionId,
      {
        stayPeriod: this.session.context.stayPeriod,
        guests: this.session.context.guests,
        selectedHotelId: this.session.context.selectedHotelId,
        promoCode: this.promoCodeValue,
      },
    )
      .pipe(
        catchError((err) => of(err)),
        tap((res) => {
          const actionFeedback =
            action === 'add'
              ? Language.getTranslation('promoCodeAdded')
              : Language.getTranslation('promoCodeRemoved');
          this.setPromoCodeFeedback(res, actionFeedback);
          if (action === 'add' && !res.error) {
            sessionStorageSetItem('promoCode', this.promoCodeValue);
            this.qwPromoCodeAdded.emit(this.promoCodeValue);
            setTimeout(() => {
              this.promoCodeLoading = false;
            }, 0);
          }
          if (action === 'add' && res.error) {
            this.promoCodeLoading = false;
          }
          if (action === 'remove' && !res.error) {
            sessionStorageRemoveItem('promoCode');
            this.promoCodeValue = '';
            this.promoCodeLoading = false;
            this.qwPromoCodeRemoved.emit('removed');
          }
        }),
        switchMap(() => {
          return SessionService.fetchSession(this.session.sessionId, {
            preventShare: true,
          });
        }),
      )
      .subscribe();
  }

  public clearInput() {
    this.promoCodeValue = '';
    this.isUserTyping = false;
    this.isPromoInvalid = false;
  }

  render() {
    const shouldBeVisible = !this.hasPromo;
    const isButtonEnabled =
      !this.hasPromo && this.promoCodeValue !== '' && this.renderTriggered;

    return (
      <Host
        class={`
      ${shouldBeVisible ? 'visible' : 'hidden'}
      ${!this.renderTriggered ? 'disable-promo' : ''}
      `}
      >
        <div class="booking--promo-code__wrapper">
          <qw-input
            class={
              this.hasPromo
                ? 'promo-code__label--to-remove'
                : 'promo-code__label--to-add'
            }
            qwInputValue={this.promoCodeValue}
            qwInputLabel={this.qwPromoCodeLabel}
            qwInputPlaceholder={Language.getTranslation('insertPromoCode')}
            qwInputCaption={
              this.promoCodeValue !== '' ? this.promoCodeFeedback : ''
            }
            onKeyUp={(event: UIEvent) => this.onChangeValue(event)}
          />
          {!this.isPromoInvalid && !this.hasPromo && (
            <QwButton
              QwButtonToAdd={true}
              QwButtonIcon={false}
              QwButtonLabel={Language.getTranslation('apply')}
              QwButtonDisabled={!isButtonEnabled}
              QwButtonOnClick={() => this.managePromo('add')}
            />
          )}
          {this.promoCodeLoading && <qw-loading qw-loading-size="20" />}
          {this.hasPromo && (
            <QwButton
              QwButtonClass="qw-button--delete"
              QwButtonLabel=""
              QwButtonOnClick={() => this.fetchSessionWithPromoCode('remove')}
            />
          )}
        </div>
      </Host>
    );
  }
}
